import React, { useEffect, useState } from 'react';
import reviewsApi from '../../services/reviewsApi';

const ReviewForm = ({ bookingId, memberId, reviewType, onSubmitSuccess, onCancel }) => {
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    
    reviewsApi.getForm(reviewType, memberId)
      .then((data) => {
        if (cancelled) return;
        setSchema(data);
        const init = {};
        (data.fields || []).forEach((f) => { init[f.key] = ''; });
        setValues(init);
        setSelectedBadges([]);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Unable to load review form.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
      
    return () => { cancelled = true; };
  }, [reviewType]);

  const onChange = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
  };

  const badgesConfig = schema?.badges_config;
  const maxBadges = badgesConfig?.max_badges ?? 0;

  const onBadgeToggle = (badgeKey) => {
    setSelectedBadges((prev) => {
      if (prev.includes(badgeKey)) {
        return prev.filter((k) => k !== badgeKey);
      }
      if (maxBadges > 0 && prev.length >= maxBadges) {
        return prev;
      }
      return [...prev, badgeKey];
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus('Submitting…');
    
    try {
      const result = await reviewsApi.submit({ 
        booking_id: bookingId, 
        member_id: memberId, 
        fields: values,
        badge_keys: selectedBadges
      });
      setStatus(
        result?.is_published || result?.publication_policy === 'immediate'
          ? 'Submitted successfully.'
          : 'Your review has been submitted and is awaiting publication.'
      );
      
      // Clear form
      const cleared = {};
      Object.keys(values).forEach((k) => cleared[k] = '');
      setValues(cleared);
      setSelectedBadges([]);
      
      // Call success callback
      if (onSubmitSuccess) {
        setTimeout(() => onSubmitSuccess(), 1000);
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Submission failed.';
      setStatus(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="review-form-loading">
        Loading…
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-form-error">
        <p>{error}</p>
        {onCancel && (
          <button onClick={onCancel} className="button">
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="review-form-container">
      <form className="review-form" onSubmit={onSubmit}>
        {(schema?.fields || []).map((f) => (
          <div key={f.key} className="review-field">
            <label htmlFor={`review-field-${f.key}`}>
              {f.label || f.key}
              {f.required && ' *'}
            </label>
            {f.type === 'rating' ? (
              <select 
                id={`review-field-${f.key}`}
                name={f.key} 
                value={values[f.key] || ''} 
                onChange={onChange(f.key)}
                required={f.required}
              >
                <option value="">Select rating</option>
                {Array.from({ 
                  length: ((f.max ?? 5) - (f.min ?? 1) + 1) 
                }, (_, i) => (i + (f.min ?? 1))).map((n) => (
                  <option key={n} value={String(n)}>
                    {n} {n === 1 ? 'Star' : 'Stars'}
                  </option>
                ))}
              </select>
            ) : (
              <>
              <textarea 
                id={`review-field-${f.key}`}
                name={f.key} 
                rows="4" 
                value={values[f.key] || ''} 
                onChange={onChange(f.key)}
                placeholder={f.placeholder || `Enter ${f.label || f.key}`}
                required={f.required}
              />
              {f.type === 'text' && f.min_words > 0 && (
                <span className="review-field-help">
                  Minimum {f.min_words} words
                </span>
              )}
              </>
            )}
          </div>
        ))}
        
        {badgesConfig?.enabled && Array.isArray(badgesConfig.badges) && badgesConfig.badges.length > 0 && (
          <div className="review-badges-section">
            <h4 className="review-badges-heading">
              Award Badges
              {maxBadges > 0 && (
                <span className="review-badges-limit">
                  {' '}({selectedBadges.length}/{maxBadges})
                </span>
              )}
            </h4>
            <p className="review-badges-description">
              {maxBadges > 0
                ? `You may select up to ${maxBadges} badge(s) to award.`
                : 'Select any badges you would like to award.'}
            </p>
            <div className="review-badges-list">
              {badgesConfig.badges.map((badge) => {
                const isSelected = selectedBadges.includes(badge.key);
                const isDisabled = badge.already_awarded ||
                  (!isSelected && maxBadges > 0 && selectedBadges.length >= maxBadges);
                return (
                  <label
                    key={badge.key}
                    className={`review-badge-item${isSelected ? ' is-selected' : ''}${isDisabled ? ' is-disabled' : ''}`}
                  >
                    <input
                      type="checkbox"
                      value={badge.key}
                      checked={isSelected}
                      disabled={isDisabled}
                      onChange={() => !isDisabled && onBadgeToggle(badge.key)}
                      aria-describedby={`review-badge-desc-${badge.key}`}
                    />
                    {badge.icon_url && (
                      <img
                        src={badge.icon_url}
                        alt=""
                        className="review-badge-icon"
                        width={24}
                        height={24}
                      />
                    )}
                    <span className="review-badge-label">{badge.label}</span>
                    {badge.already_awarded && (
                      <span className="review-badge-awarded-note">
                        Already awarded
                      </span>
                    )}
                    {badge.description && (
                      <span id={`review-badge-desc-${badge.key}`} className="review-badge-description">
                        {badge.description}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}
        
        <div className="review-form-help">
          <p>Please provide honest, respectful feedback. Required fields are marked with *.</p>
        </div>
        
        <div className="review-form-actions">
          <button 
            type="submit" 
            className="button button-primary" 
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Review'}
          </button>
          {onCancel && (
            <button 
              type="button" 
              onClick={onCancel} 
              className="button" 
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>
        
        {status && (
          <div className="review-form-status" role="status" aria-live="polite">
            {status}
          </div>
        )}
      </form>
    </div>
  );
};

export default ReviewForm;
